import React, { useState, useEffect } from "react";
import axios from "axios";

/**
 * InstructionForSinglePart
 * Dùng chung cho cả phần Reading và Writing.
 *
 * Props:
 * - scenarioId: ID của scenario hiện tại
 * - instructionType: "reading" hoặc "writing"
 * - onComplete: callback khi người dùng bấm Next hoặc hết instruction
 */

const InstructionForSinglePart = ({
  scenarioId,
  instructionType = "reading",
  onComplete,
}) => {
  const [tasks, setTasks] = useState([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tasks theo scenario + type
  useEffect(() => {
    if (!scenarioId) return;
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/scenarios/tasks/${scenarioId}?task_type=${instructionType}`);
        const sorted = (res.data || []).sort((a, b) => (a.task_order ?? 0) - (b.task_order ?? 0));
        setTasks(sorted);
      } catch (err) {
        console.error("❌ Error fetching tasks:", err);
        setError("Failed to load instruction tasks.");
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [scenarioId, instructionType]);


  // UI trạng thái load
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 animate-pulse">
          Loading {instructionType} instructions...
        </p>
      </div>
    );
  }

  // UI lỗi
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  // Không có task nào
  if (!tasks.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">
          No instruction found for this {instructionType} section.
        </p>
      </div>
    );
  }

  const task = tasks[currentTaskIndex];

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg p-8 border border-gray-200 transition-all">
      {/* Header */}
      <h2 className="text-2xl font-bold text-gray-800 mb-3">
        {task.task_name || "Instruction"}
      </h2>

      {task.task_description && (
        <p className="text-gray-600 mb-6 leading-relaxed">
          {task.task_description}
        </p>
      )}

      {/* Layout: Media bên trái - nội dung bên phải */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 items-start">
        {/* LEFT - Media */}
        <div className="flex justify-center lg:justify-start">
          {/* ƯU TIÊN video_file */}
          {task.video_file ? (() => {
            const ext = task.video_file.split(".").pop().toLowerCase();
            const isVideo = ["mp4", "mov", "webm"].includes(ext);
            const isImage = ["jpg", "jpeg", "png", "gif"].includes(ext);
            const fileSrc = task.video_file.startsWith("/AI_Video/")
              ? task.video_file
              : `/AI_Video/${task.video_file}`;

            if (isVideo) {
              return (
                <video
                  controls
                  className="rounded-2xl shadow-md border w-full max-w-[380px]"
                  src={fileSrc}
                  poster={
                    task.poster_file
                      ? `/AI_Video/${task.poster_file}`
                      : undefined
                  }
                />
              );
            } else if (isImage) {
              return (
                <img
                  src={fileSrc}
                  alt={task.instruction_title || "Task"}
                  className="rounded-2xl shadow-md border w-full max-w-[380px] object-contain"
                />
              );
            } else {
              return (
                <div className="w-full max-w-[380px] h-[220px] flex items-center justify-center bg-gray-50 border rounded-2xl text-gray-400">
                  Unsupported media format
                </div>
              );
            }
          })()
            // NẾU KHÔNG CÓ video_file → fallback sang image_file
            : task.image_file ? (
              <img
                src={
                  task.image_file.startsWith("/AI_Video/")
                    ? task.image_file
                    : `/AI_Video/${task.image_file}`
                }
                alt={task.instruction_title || "Task"}
                className="rounded-2xl shadow-md border w-full max-w-[380px] object-contain"
              />
            ) : (
              <div className="w-full max-w-[380px] h-[220px] flex items-center justify-center bg-gray-50 border rounded-2xl text-gray-400">
                No media available
              </div>
            )}
        </div>


        {/* RIGHT - Nội dung hướng dẫn */}
        <div className="flex flex-col space-y-3">
          {task.instruction_title && (
            <h3 className="text-xl font-semibold text-gray-800">
              {task.instruction_title}
            </h3>
          )}

          {task.instruction_content ? (
            <div
              className="text-gray-700 leading-relaxed prose max-w-none"
              dangerouslySetInnerHTML={{
                __html: task.instruction_content,
              }}
            />
          ) : (
            <p className="text-gray-500 italic">No additional content.</p>
          )}
        </div>
      </div>

      {/* Footer Button */}
      <div className="mt-10 flex justify-center">
        <button
          onClick={() => {
            const isLast = currentTaskIndex + 1 >= tasks.length;
            if (isLast) {
              onComplete?.();
            } else {
              setCurrentTaskIndex((prev) => prev + 1);
            }
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-xl shadow-md transition duration-200"
        >
          {instructionType === "reading"
            ? "Proceed to Reading Passage"
            : "Proceed to Writing Passage"}
        </button>
      </div>
    </div>
  );
};

export default InstructionForSinglePart;
